/**
 * El nombre que identifica a una dirección de **facturación**.
 *
 * No se puede armar como `firstname + lastname`: en la fila de facturación esas dos columnas
 * no son "nombre" y "apellido". La convención de todo el código de Magento es que
 * `lastname` lleva la **razón social** —`MarketplaceQuote/Controller/Quote/Checkout.php`
 * (`setLastname($billing['company'])`), su gemelo `AdminImpersonate/PlaceOrder.php`, y
 * `MarketplaceCheckout/Controller/Address/EditAddress.php` (`setLastname($company ?: '-')`)—
 * y que el nombre de la persona viaja **completo** en `firstname`. La columna `company` de
 * `sales_order_address` nunca se escribe: medido en las 15.351 filas del padrón, está vacía
 * en el 100%.
 *
 * Concatenar los dos campos es lo que producía los duplicados reportados, que son **el mismo
 * bug** visto desde los dos lados:
 *
 *   - comprando desde Magento, el storefront manda el nombre completo en `firstname`
 *     ("Oliverio Santa Cruz") y lo que quedó en el input de razón social como `company`
 *     ("Cruz") → la orden queda `firstname="Oliverio Santa Cruz"` / `lastname="Cruz"` y se
 *     leía **"Oliverio Santa Cruz Cruz"**: parece el apellido repetido;
 *   - comprando desde el Admin, `BillingDataModal` splitea bien pero además guarda el nombre
 *     completo en `company` → la orden queda `firstname="Oliverio"` /
 *     `lastname="Oliverio Santa Cruz"` y se leía **"Oliverio Oliverio Santa Cruz"**: parece
 *     el nombre repetido.
 *
 * Y en las facturas A/B de empresa `firstname` es el relleno `'-'` (0 de 2.611 filas de tipo
 * 2/3/4 tienen otra cosa), así que la concatenación mostraba **"- RAZÓN SOCIAL"**.
 *
 * El criterio es el mismo que ya usan `QuoteAddressBookPopup`, `quoteDetail` y
 * `BillingDataModal`: en facturación manda la razón social. Acá se agrega el desempate del
 * consumidor final, que es lo que esas tres pantallas no necesitaban resolver.
 *
 * Se arregla **en el render y no en el dato**: `lastname` como razón social es lo que leen la
 * factura de AFIP y el resto de los consumidores, así que reescribirlo cambiaría lo que se
 * emite. Tampoco hay backfill: el nombre completo sigue estando entero en la fila, sólo que
 * repartido, y esta función lo reconstruye.
 */

/** Los tipos de condición de IVA en que la identidad de la factura es una empresa. */
const COMPANY_BILLING_TYPES = new Set(["2", "3", "4"]);

/**
 * Varios flujos guardan un guion como relleno en los campos que no aplican, así que un `"-"`
 * es un campo VACÍO y no un dato. Mismo criterio que `cleanField` de `QuoteAddressBookPopup`.
 */
const clean = (value?: string | null): string => {
  const text = String(value ?? "").trim();
  return /^-+$/.test(text) ? "" : text;
};

/** Compara nombres ignorando mayúsculas y espacios de más, que es como llegan tipeados. */
const normalize = (name: string): string =>
  name.toLowerCase().replace(/\s+/g, " ").trim();

/**
 * `true` si `needle` ya está contenido en `haystack` **como palabras completas**.
 *
 * El borde de palabra importa: sin él "Ana" se daría por contenida en "Susana Pérez" y el
 * nombre se mostraría incompleto.
 */
const containsWords = (haystack: string, needle: string): boolean => {
  const a = normalize(haystack);
  const b = normalize(needle);
  if (!b) return true;
  return (
    a === b ||
    a.startsWith(`${b} `) ||
    a.endsWith(` ${b}`) ||
    a.includes(` ${b} `)
  );
};

export interface BillingNameFields {
  firstname?: string | null;
  lastname?: string | null;
  company?: string | null;
  billingType?: string | number | null;
}

/**
 * El nombre de facturación listo para mostrar, o `""` si la dirección no trae ninguno.
 *
 * Devuelve `""` —y no `"-"`— para que los `{name || "-"}` que ya existen en las pantallas
 * caigan solos a su rama de "sin dato", igual que `displayEmail`.
 */
export const billingDisplayName = (
  address?: BillingNameFields | null,
): string => {
  if (!address) return "";

  const firstname = clean(address.firstname);
  const lastname = clean(address.lastname);
  const company = clean(address.company);

  // Empresa: la razón social ES la identidad de la factura, y el nombre del titular de la
  // cuenta al lado no aporta. `lastname` antes que `company` porque es donde el place-order
  // la escribe; `company` primero por si algún día se puebla la columna correcta.
  if (COMPANY_BILLING_TYPES.has(String(address.billingType ?? ""))) {
    return company || lastname || firstname;
  }

  // Consumidor final: no hay razón social, y el nombre completo puede haber quedado en
  // cualquiera de los dos campos. El que contiene al otro es el completo.
  if (!lastname) return firstname;
  if (!firstname) return lastname;
  if (containsWords(firstname, lastname)) return firstname;
  if (containsWords(lastname, firstname)) return lastname;

  // Nombre y apellido genuinamente separados: el caso sano.
  return `${firstname} ${lastname}`;
};
